import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Select, SelectOptionType } from '@bp/ui-components';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import { ProfileRoles } from '@bp/pim-auth-constants';
import { useMemo } from 'react';
import { profileRoleAsString } from '../../utils/roleAsString';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { showErrorToast } from '../../utils/showErrorToast';
import { ProfileTableData } from '../../pages/Institution/subpages/InstitutionPersonsSubpage';
import { graphql } from 'client/gql';
import { useMutation } from 'urql';

const BpChangeProfileRoleMutationDocument = graphql(`
  mutation UpdateProfileRole($uuid: String!, $role: String!) {
    updateProfileRole(uuid: $uuid, role: $role) {
      entitiesUpdated
    }
  }
`);

type ChangeRoleFormProps = { profile: ProfileTableData | null; setModalClosed: () => void };

type ChangeRoleValues = {
  role: string;
};

export const ChangeProfileRoleForm = ({ profile, setModalClosed }: ChangeRoleFormProps) => {
  const { t } = useTranslation();
  const [, updateProfileRoles] = useMutation(BpChangeProfileRoleMutationDocument);

  const allowedNewRoles = Object.values(ProfileRoles);
  const formikValues: ChangeRoleValues = {
    role:
      profile?.organizationRoles.find((r) => allowedNewRoles.includes((r as ProfileRoles) ?? '')) ?? ProfileRoles.Other,
  };

  const handleSubmit = async (values: ChangeRoleValues) => {
    if (profile) {
      const resp = await updateProfileRoles({ role: values.role, uuid: profile.uuid });
      resp.error ? showErrorToast(resp.error) : showSuccessToast(t('common.saved'));
    }
    setModalClosed();
  };

  const profileRoleSelectOpts = useMemo(() => {
    return [ProfileRoles.Teacher, ProfileRoles.Student, ProfileRoles.Parent, ProfileRoles.Other].map(
      (r) => ({ label: profileRoleAsString(r), value: r }) as SelectOptionType,
    );
  }, []);

  return (
    <Formik onSubmit={handleSubmit} initialValues={formikValues}>
      {({ resetForm, isSubmitting, errors, setFieldValue, values }) => {
        const onClose = () => {
          resetForm();
          setModalClosed();
        };

        return (
          <Form>
            <Select
              label={t('profiles.role')}
              value={profileRoleSelectOpts.find((v) => v.value === values.role)}
              options={profileRoleSelectOpts}
              onChange={(opt) => setFieldValue('role', (opt as SelectOptionType).value)}
              name={'role'}
              isSearchable={true}
            />

            <ModalBottomButtons
              isLoading={isSubmitting}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
